'use client';

import { useEffect, useRef, useState } from 'react';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useCurrentWalletConnectionState from 'src/hooks/useCurrentWalletConnectionState';
import useHasConnectedWalletBefore from 'src/hooks/useHasConnectedWalletBefore';
import { AUTOCONNECT_DELAY_MS } from 'src/hooks/useCustomWalletAutoconnect';
import { SetTimeoutResult } from 'src/utils/UtilityTypes';

/**
 * App is "initial-loading" when:
 * - Drift client isn't ready yet, OR
 * - Wallet is actively connecting, OR
 * - We expect an autoconnect attempt (based on prior wallet usage) and we're still within the autoconnect window.
 */
export default function useAppInitialLoad() {
	const driftReady = useDriftClientIsReady();
	const { instance: walletState } = useCurrentWalletConnectionState();
	const [hasConnectedBefore] = useHasConnectedWalletBefore();

	const [awaitingAutoConnect, setAwaitingAutoConnect] = useState(false);
	const timerRef = useRef<SetTimeoutResult>(null);

	// Start a short "autoconnect window" if user has a prior wallet and we're not connected yet.
	useEffect(() => {
		const hadPrevWallet =
			typeof window !== 'undefined' && !!window.localStorage?.walletName;

		if (!hasConnectedBefore || !hadPrevWallet) {
			setAwaitingAutoConnect(false);
			return;
		}

		// If we're already connecting/connected, no need to wait for an autoconnect window.
		if (
			walletState.Connecting ||
			walletState.AdapterConnected ||
			walletState.FullyConnected
		) {
			setAwaitingAutoConnect(false);
			return;
		}

		setAwaitingAutoConnect(true);

		timerRef.current = setTimeout(() => {
			setAwaitingAutoConnect(false);
		}, AUTOCONNECT_DELAY_MS);

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
		};
	}, [
		hasConnectedBefore,
		walletState.Connecting,
		walletState.AdapterConnected,
		walletState.FullyConnected,
	]);

	// If connecting begins or we reach a fully-connected state, stop "awaiting autoconnect".
	useEffect(() => {
		if (walletState.FullyConnected) {
			setAwaitingAutoConnect(false);

			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
		}
	}, [walletState.FullyConnected]);

	const isInitialLoading = !driftReady || awaitingAutoConnect;

	return { isInitialLoading, isReady: !isInitialLoading };
}
